import { DestroyRef, Injectable, inject } from '@angular/core';
import {
  Observable,
  catchError,
  debounceTime,
  of as just,
  switchMap,
  tap,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { makeObjectWithNoNullValues } from '@app-shared/functions/helpers.functions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { emitOnce } from '@ngneat/elf';
import { CountryRepository } from 'src/app/entities/countries/state/country.repository';

import { ManufacturerRepository } from './manufacturer.repository';
import { IManufacturer } from './manufacturer.model';

@Injectable({ providedIn: 'root' })
export class ManufacturersService {
  private readonly manufacturersRepo = inject(ManufacturerRepository);
  private readonly countryRepo = inject(CountryRepository);

  private readonly destroyRef = inject(DestroyRef);
  private readonly http = inject(HttpClient);

  init(): void {
    this.manufacturersRepo.ui$
      .pipe(
        debounceTime(0),
        switchMap(() => this.getManufacturers()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private getManufacturers(): Observable<IManufacturer[] | null> {
    const params = makeObjectWithNoNullValues({
      q: this.manufacturersRepo.getStoreProp('ui').searchTerm || null,
      name_first_letter:
        this.manufacturersRepo.getStoreProp('ui').letter || null,
      country: this.countryRepo.getActiveId()?.toString() || null,
    });

    if (!params['q'] && !params['name_first_letter']) {
      this.manufacturersRepo.setStoreProp('manufacturers', []);

      return just(null);
    }

    emitOnce(() => {
      this.manufacturersRepo.setStoreProp('loading', true);
      this.manufacturersRepo.setStoreProp('error', false);
    });

    return this.http
      .get<IManufacturer[]>(environment.apiUrl + `/product/manufacturers`, {
        params: params,
      })
      .pipe(
        tap(response => {
          emitOnce(() => {
            this.manufacturersRepo.setStoreProp('manufacturers', response);
            this.manufacturersRepo.setStoreProp('loading', false);
          });
        }),
        catchError(error => {
          emitOnce(() => {
            this.manufacturersRepo.setStoreProp('loading', false);
            this.manufacturersRepo.setStoreProp('error', true);
          });

          return just(error);
        }),
      );
  }

  resetStore(): void {
    this.manufacturersRepo.resetStore();
  }

  /**
   * Save the selection of manufacturers
   *
   * @param selection - selected manufacturers
   */
  setSelectedManufacturers(selection: IManufacturer[]): void {
    emitOnce(() => {
      let selectedIds: number[] = [];

      selection.forEach(s => {
        const selectionValue = s.id;

        if (typeof selectionValue === 'number') {
          selectedIds.push(selectionValue);
        } else {
          selectedIds = selectedIds.concat(selectionValue);
        }
      });

      this.manufacturersRepo.setStoreProp('selectedManufacturers', selection);
      this.manufacturersRepo.setStoreProp(
        'selectedManufacturersIDs',
        selectedIds,
      );
    });
  }
}
